import React, { useState, useEffect, useMemo, useCallback } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay } from "swiper"
import { useAnnouncement } from "@app/hooks/useAnnouncement"
import { Flex, Text } from "@chakra-ui/react"
import Link from "@app/components/Link"

import "swiper/css"
import "swiper/css/autoplay"
import { useAppContext } from "@app/providers/app"
import { useRoutes } from "@app/hooks/useRoutes"
import { colors } from "@app/theme/colors"
import { useNewsletter } from "@app/hooks/useNewsletter"

import { Location } from "@root/types/global"

type Props = {
  location: Location
}

const Announcement: React.FC<Props> = ({ location }) => {
  const { anyUrlResolver } = useRoutes()
  const { items: rawItems, defaultBgColour } = useAnnouncement()
  const { state, dispatch, announcementRef } = useAppContext()
  const { handleOpen } = useNewsletter(location)
  const fontColour = defaultBgColour === colors.primary.midGrey ? "primary.white" : "primary.midGrey"

  const [isVisible, setIsVisible] = useState(false)

  const items = useMemo(() => rawItems?.map(item => ({ ...item, link: anyUrlResolver(item) })), [anyUrlResolver, rawItems])

  const onHeaderToggle = useCallback(() => {
    state.activeHeader &&
      dispatch({
        type: "header",
        payload: null,
      })
  }, [dispatch, state.activeHeader])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsVisible(true)
    }, 2000)

    return () => clearTimeout(timeoutId)
  }, [])

  return items?.length > 1 ? (
    <Flex
      ref={announcementRef}
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      bgColor={defaultBgColour}
      zIndex={1100}
      height="40px"
      onClick={() => onHeaderToggle()}
      borderBottom="1px solid"
      borderColor="grey.lightest"
    >
      <Swiper
        modules={[Autoplay]}
        loop
        slidesPerView={1}
        autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
        speed={1750}
      >
        {items?.map((item: any, index: number) => (
          <SwiperSlide key={item?._key || index}>
            {item?.type !== "activatesSubscribePopup" ? (
              <Text
                {...(item?.link?.url ? { as: Link, to: item.link.url } : { as: "p" })}
                textDecoration={item?.link?.url && "underline"}
                size="sm"
                variant="medium"
                color={fontColour}
                display="inline"
                opacity={isVisible ? 1 : 0}
                transition="opacity 0.5s ease"
              >
                {item?.title}
              </Text>
            ) : (
              <Text
                size="sm"
                variant="medium"
                color={fontColour}
                display="inline"
                opacity={isVisible ? 1 : 0}
                transition="opacity 0.5s ease"
                cursor="pointer"
                onClick={handleOpen}
              >
                {item?.title}
              </Text>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </Flex>
  ) : (
    <Flex
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      bgColor={defaultBgColour}
      zIndex={1100}
      height="40px"
      opacity={isVisible ? 1 : 0}
      transition="opacity 0.5s ease"
    >
      <Text size="sm" color={fontColour} display="inline" variant="medium">
        {items[0]?.title}
      </Text>
    </Flex>
  )
}

export default React.memo(Announcement)
